import React, {
    createElement,
    useRef,
    useEffect,
    useMemo,
} from 'react';
import PropTypes from 'prop-types';
import classnames from 'classnames';
import { Card } from '@material-ui/core';import { makeStyles } from '@material-ui/core/styles';
import { ThemeProvider } from '@material-ui/styles';
import { useHistory } from 'react-router-dom';
import { useCheckAuth } from 'ra-core';

import DefaultNotification from './Notification';
import DefaultLoginForm from './LoginForm';

import {
    createMuiTheme as createLegacyModeTheme,
    unstable_createMuiStrictModeTheme as createStrictModeTheme,
} from '@material-ui/core/styles';


const defaultTheme = {
    palette: {
        secondary: {
            light: '#6ec6ff',
            main: '#2196f3',
            dark: '#0069c0',
            contrastText: '#fff',
        },
    },
    typography: {
        h6: {
            fontWeight: 400,
        },
    },
    sidebar: {
        width: 240,
        closedWidth: 55,
    },
    overrides: {
        MuiFilledInput: {
            root: {
                backgroundColor: 'rgba(0, 0, 0, 0.04)',
                '&$disabled': {
                    backgroundColor: 'rgba(0, 0, 0, 0.04)',
                },
            },
        },
        MuiButtonBase: {
            root: {
                '&:hover:active::after': {
                    // recreate a static ripple color
                    // use the currentColor to make it work both for outlined and contained buttons
                    // but to dim the background without dimming the text,
                    // put another element on top with a limited opacity
                    content: '""',
                    display: 'block',
                    width: '100%',
                    height: '100%',
                    position: 'absolute',
                    top: 0,
                    right: 0,
                    backgroundColor: 'currentColor',
                    opacity: 0.3,
                    borderRadius: 'inherit',
                },
            },
        },
    },
    props: {
        MuiButtonBase: {
            // disable ripple for perf reasons
            disableRipple: true,
        },
    },
};

export const createMuiTheme =
    process.env.NODE_ENV === 'production'
        ? createLegacyModeTheme
        : createStrictModeTheme;

const useStyles = makeStyles(
    theme => ({
        main: {
            display: 'flex',
            flexDirection: 'column',
            minHeight: '100vh',
            height: '1px',
            alignItems: 'center',
            justifyContent: 'flex-start',
            backgroundRepeat: 'no-repeat',
            backgroundSize: 'cover',
            backgroundColor: '#F1F1F1',
        },
        card: {
            minWidth: 300,
            marginTop: '6em',
            padding: '2em'
        },
        avatar: {
            margin: '1em',
            display: 'flex',
            justifyContent: 'center',
        },
        icon: {
            backgroundColor: theme.palette.secondary[500],
        },
    }),
    { name: 'RaLogin' }
);

const Login = props => {
    const {
        theme,
        title,
        classes: classesOverride,
        className,
        children,
        notification,
        backgroundImage,
        ...rest
    } = props;

    const containerRef = useRef(null);
    const classes = useStyles(props);
    const muiTheme = useMemo(() => createMuiTheme(theme), [theme]);
    let backgroundImageLoaded = false;
    const checkAuth = useCheckAuth();
    const history = useHistory();

    useEffect(() => {
        checkAuth({}, false)
            .then(() => {
                history.push('/');
            })
            .catch(() => {
                // stay on login page
            });
    }, [checkAuth, history]);

    const updateBackgroundImage = () => {
        if (!backgroundImageLoaded && containerRef.current) {
            containerRef.current.style.backgroundImage = `url(${backgroundImage})`;
            backgroundImageLoaded = true;
        }
    };

    const lazyLoadBackgroundImage = () => {
        if (backgroundImage) {
            const img = new Image();
            img.onload = updateBackgroundImage;
            img.src = backgroundImage;
        }
    };

    useEffect(() => {
        if (!backgroundImageLoaded) {
            lazyLoadBackgroundImage();
        }
    });

    var imgName = window.location.hostname.replaceAll('.','');
    
    return (
        <ThemeProvider theme={muiTheme}>
        <div
            className={classnames(classes.main, className, 'login')}
            {...rest}
            ref={containerRef}
        >
            <Card className={`${classes.card} card`}>
                <div className={`${classes.avatar} login-image`}>
                    <img
                        src={`/img/${window.location.hostname.replaceAll('.', '')}_200x200.png`}
                        alt="logo"
                    />
                </div>
                {children}
            </Card>

            {notification ? createElement(notification) : null}
        </div>
    </ThemeProvider>
    );
};

Login.propTypes = {
    backgroundImage: PropTypes.string,
    children: PropTypes.node,
    classes: PropTypes.object,
    className: PropTypes.string,
    theme: PropTypes.object,
};

Login.defaultProps = {
    theme: defaultTheme,
    children: <DefaultLoginForm />,
    notification: DefaultNotification,
};




export default Login;
